import React from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

// Fix for default markers in React Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface MapDisplayProps {
  latitude: number;
  longitude: number;
  propertyName: string;
  address?: string;
  height?: number;
}

export const MapDisplay: React.FC<MapDisplayProps> = ({
  latitude,
  longitude,
  propertyName,
  address,
  height = 300,
}) => {
  const { t } = useTranslation();

  // Convert to numbers and validate
  const lat = Number(latitude);
  const lng = Number(longitude);

  if (!lat || !lng || isNaN(lat) || isNaN(lng)) {
    return (
      <Box sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {t('location_not_available')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ height, width: '100%', border: '1px solid #ccc', borderRadius: 1 }}>
        <MapContainer
          center={[lat, lng]}
          zoom={15}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          <Marker position={[lat, lng]}>
            <Popup>
              <div>
                <Typography variant="subtitle2" fontWeight="bold">
                  {propertyName}
                </Typography>
                {address && (
                  <Typography variant="body2" color="text.secondary">
                    {address}
                  </Typography>
                )}
              </div>
            </Popup>
          </Marker>
        </MapContainer>
      </Box>
      <Box sx={{ mt: 1 }}>
        <Typography variant="caption" color="text.secondary" display="block">
          {t('coordinates')}: {lat.toFixed(6)}, {lng.toFixed(6)}
        </Typography>
        <Typography variant="caption" color="primary" display="block" sx={{ mt: 0.5 }}>
          <a 
            href={`https://www.google.com/maps?q=${lat},${lng}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            🌐 {t('view_on_google_maps')}
          </a>
          {' | '}
          <a 
            href={`https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}&zoom=15`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            🗺️ {t('view_on_openstreetmap')}
          </a>
        </Typography>
      </Box>
    </Box>
  );
};
